'use client';

import { useState } from 'react';
import type { NewsletterIssueData, NewsletterMastheadBlockProps } from '../types';
import { theme, sectionColors } from '../theme';
import { issueLabel, mastheadTitle, pickIssue, readBrowserId, readStorage, writeStorage } from './newsletterShared';

/**
 * The masthead of "The [Community] Letter" (V3.2, docs/V3_LAUNCH_PLAN.md §5): the title, the
 * issue label, the publisher line ("by {agent}") and a Follow button. CLIENT component,
 * framework-agnostic (plain elements, no next/*).
 *
 * Follow → the `community:<slug>` stream. In order:
 *   1. `window.bltFollow(slug)` when the host installs one (the community page's own button);
 *   2. a same-origin POST to /api/visitor/communities/{slug}/follow with the visitor JWT the /my
 *      page keeps (localStorage `blt_visitor_jwt`) — the tenant app's proxy, which forwards the
 *      token untouched to the gateway;
 *   3. signed out (no token, or a 401): the identify prompt — an email → POST /api/visitor/identify;
 *      the wish is held in localStorage (`blt_pending_community_follow`) for the /my page to act
 *      on once the magic link is tapped.
 * No copy here characterises the community — only the Letter and its numbers.
 */

export const VISITOR_JWT_KEY = 'blt_visitor_jwt';
export const PENDING_FOLLOW_KEY = 'blt_pending_community_follow';

type Phase = 'idle' | 'saving' | 'following' | 'email' | 'sending' | 'link-sent' | 'error';

declare global {
  interface Window {
    bltFollow?: (slug: string) => Promise<boolean | void> | boolean | void;
  }
}

export function NewsletterMastheadBlock(props: NewsletterMastheadBlockProps & { issues?: NewsletterIssueData[]; publisher?: string | null }) {
  const { subheading, buttonText = 'Follow', hideFollow, issues = [], publisher = null } = props;
  const issue = pickIssue(issues, { communitySlug: props.communitySlug || null });
  const communityName = (props.communityName ?? '').trim() || issue?.sections.community.name || '';
  const slug = (props.communitySlug ?? '').trim() || issue?.community_slug || '';
  const publisherName = (props.publisherName ?? '').trim() || (publisher ?? '').trim();
  const title = mastheadTitle(props.heading, communityName);
  const colors = sectionColors(props, { bg: theme.surface, text: theme.text });

  const [phase, setPhase] = useState<Phase>('idle');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const follow = async () => {
    if (!slug) return;
    setMessage(null);
    if (typeof window !== 'undefined' && typeof window.bltFollow === 'function') {
      setPhase('saving');
      try {
        const r = await window.bltFollow(slug);
        setPhase(r === false ? 'idle' : 'following');
      } catch {
        setPhase('idle');
      }
      return;
    }
    const jwt = readStorage(VISITOR_JWT_KEY);
    if (jwt) {
      setPhase('saving');
      try {
        const res = await fetch(`/api/visitor/communities/${encodeURIComponent(slug)}/follow`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` } });
        if (res.ok) { setPhase('following'); return; }
        if (res.status !== 401) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          setMessage(body?.error || 'Could not follow — try again.');
          setPhase('error');
          return;
        }
      } catch {
        setMessage('Could not reach the server — try again.');
        setPhase('error');
        return;
      }
    }
    setPhase('email');
  };

  const identify = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    const addr = email.trim();
    if (!addr) return;
    setPhase('sending');
    try {
      const res = await fetch('/api/visitor/identify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_id: readBrowserId(), email: addr, community_slug: slug, source: 'community_follow' }),
      });
      if (!res.ok) {
        setMessage('That email doesn’t look right — try again.');
        setPhase('email');
        return;
      }
      writeStorage(PENDING_FOLLOW_KEY, JSON.stringify({ slug, name: communityName, saved_at: Date.now() }));
      setPhase('link-sent');
    } catch {
      setMessage('Something went wrong — try again.');
      setPhase('email');
    }
  };

  const btn = 'inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-semibold transition-opacity disabled:opacity-60';

  return (
    <section style={{ ...colors, fontFamily: theme.bodyFont }} className="px-6 py-12" data-block="newsletter-masthead">
      <div className="mx-auto max-w-3xl border-b-2 pb-8 text-center" style={{ borderColor: theme.primary }}>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] opacity-70">{issue ? issueLabel(issue) : 'A monthly letter'}</p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight md:text-5xl" style={{ fontFamily: theme.headingFont }}>{title}</h1>
        {publisherName ? <p className="mt-2 text-sm opacity-80" data-testid="masthead-publisher">by {publisherName}</p> : null}
        {subheading ? <p className="mx-auto mt-4 max-w-xl text-base opacity-80">{subheading}</p> : null}
        {!hideFollow && slug ? (
          <div className="mt-6 flex flex-col items-center gap-2" data-testid="masthead-follow">
            {phase === 'following' ? (
              <span className={btn} style={{ border: `1px solid ${theme.primary}`, color: theme.primary }}>Following {communityName || 'this community'}</span>
            ) : phase === 'link-sent' ? (
              <p className="text-sm opacity-80">Check your inbox — tap the sign-in link and you&rsquo;ll follow {communityName || 'this community'}: one Letter a month.</p>
            ) : phase === 'email' || phase === 'sending' ? (
              <form onSubmit={identify} className="flex flex-wrap items-center justify-center gap-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  aria-label="Email address"
                  className="rounded-full border border-black/15 bg-white px-4 py-2 text-sm text-gray-900"
                />
                <button type="submit" disabled={phase === 'sending'} className={`blk-btn ${btn}`}>
                  {phase === 'sending' ? 'Sending…' : 'Follow by email'}
                </button>
              </form>
            ) : (
              <button type="button" onClick={follow} disabled={phase === 'saving'} className={`blk-btn ${btn}`}>
                {phase === 'saving' ? 'Saving…' : buttonText}
              </button>
            )}
            {message ? <p className="text-xs text-red-700" role="alert">{message}</p> : null}
            {phase === 'idle' ? <p className="text-xs opacity-60">One email a month: the market numbers, what just listed and sold, and what is coming up.</p> : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
